import { Check, Minus } from 'lucide-react';
import { forwardRef, InputHTMLAttributes } from 'react';

import { cn } from './cn';

type NativeToggleProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>;

/**
 * A native checkbox drawn in the brand colour. Use it where a handler reads
 * `event.target.checked`; `Checkbox` is the Radix one for new code.
 * Check / indeterminate marks are Lucide icons overlaid on the native input.
 */
export const CheckboxInput = forwardRef<HTMLInputElement, NativeToggleProps>(({ className, ...props }, ref) => (
  <span className="relative inline-flex size-4 shrink-0 align-middle">
    <input
      ref={ref}
      type="checkbox"
      className={cn(
        'peer size-4 cursor-pointer appearance-none rounded-sm border border-line-strong bg-surface transition-colors',
        'hover:border-brand checked:border-brand checked:bg-brand',
        'indeterminate:border-brand indeterminate:bg-brand',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
    <Check
      aria-hidden="true"
      strokeWidth={3}
      className="pointer-events-none absolute inset-0 m-auto size-3 text-ink-inverse opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0"
    />
    <Minus
      aria-hidden="true"
      strokeWidth={3}
      className="pointer-events-none absolute inset-0 m-auto size-3 text-ink-inverse opacity-0 peer-indeterminate:opacity-100"
    />
  </span>
));
CheckboxInput.displayName = 'CheckboxInput';

/** A native checkbox drawn as a switch, so handlers keep reading `event.target.checked`. */
export const SwitchInput = forwardRef<HTMLInputElement, NativeToggleProps>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    type="checkbox"
    role="switch"
    className={cn(
      'relative h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-line-strong transition-colors',
      'before:absolute before:left-0.5 before:top-0.5 before:size-4 before:rounded-full before:bg-surface before:shadow-sm before:transition-transform',
      'checked:bg-brand checked:before:translate-x-4',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  />
));
SwitchInput.displayName = 'SwitchInput';
